import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function loadGisScript() {
  return new Promise((resolve, reject) => {
    if (window.google?.accounts?.id) return resolve();
    const existing = document.getElementById("google-gis");
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.id = "google-gis";
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export default function Login() {
  const { user, loginWithGoogle } = useAuth();
  const nav = useNavigate();
  const btnRef = useRef(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (user) nav("/dashboard", { replace: true });
  }, [user, nav]);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) {
      setError("Google login is not configured (REACT_APP_GOOGLE_CLIENT_ID missing).");
      return;
    }
    let cancelled = false;
    loadGisScript()
      .then(() => {
        if (cancelled || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: async (resp) => {
            try {
              const u = await loginWithGoogle(resp.credential);
              toast.success(`Welcome, ${u.name}`);
              nav("/dashboard", { replace: true });
            } catch (e) {
              toast.error(e.response?.data?.detail || "Login failed");
            }
          },
        });
        if (btnRef.current) {
          window.google.accounts.id.renderButton(btnRef.current, {
            theme: "outline",
            size: "large",
            text: "signin_with",
            shape: "rectangular",
            width: 320,
          });
        }
      })
      .catch(() => setError("Could not load Google sign-in. Check your connection."));
    return () => { cancelled = true; };
  }, [loginWithGoogle, nav]);

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", display: "flex", alignItems: "center", justifyContent: "center", padding: "2rem", fontFamily: "var(--font-sans)" }} data-testid="login-page">
      <div style={{ maxWidth: 420, width: "100%" }}>
        <div style={{ textAlign: "center", marginBottom: 40 }}>
          <div className="inline-flex items-center gap-3 mb-4">
            <div style={{ width: 42, height: 42, borderRadius: 8, background: "var(--primary)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "Cabinet Grotesk", fontWeight: 800, fontSize: 15, letterSpacing: "-0.02em" }}>ZPM</div>
            <div style={{ textAlign: "left" }}>
              <div className="font-display" style={{ fontWeight: 800, fontSize: 22, letterSpacing: "-0.02em" }}>Zalani Paper Mart</div>
              <div className="overline" style={{ fontSize: 10, marginTop: 2 }}>Sales CRM</div>
            </div>
          </div>
          <h1 className="font-display" style={{ fontSize: 32, fontWeight: 700, letterSpacing: "-0.02em", margin: 0 }}>
            Sign in
          </h1>
          <div style={{ fontSize: 14, color: "var(--text-2)", marginTop: 6 }}>
            Use your authorised Google account to continue
          </div>
        </div>

        <div className="card-ledger" style={{ padding: 32, display: "flex", flexDirection: "column", alignItems: "center", gap: 16 }} data-testid="google-login-card">
          <div ref={btnRef} data-testid="google-signin-button" />
          {error && (
            <div style={{ fontSize: 12, color: "var(--danger)", textAlign: "center" }} data-testid="login-error">{error}</div>
          )}
          <div style={{ fontSize: 11, color: "var(--text-2)", textAlign: "center", marginTop: 4 }}>
            Only whitelisted team members can sign in.<br />Ask an admin to add your Google email.
          </div>
        </div>
      </div>
    </div>
  );
}
